<template>
    <div>
        <Banner :bannerMessage="bannerMessage" />

        <div class="container">
            <div v-if="showVerifiedErrorMessage || showVerifiedMessage || showPasswordResetMessage">
                <div v-if="showVerifiedErrorMessage" class="mb-5 pb-4">
                    <ErrorMessage :errorNotification="errorVerifiedMsg" />
                </div>
                <div v-if="showVerifiedMessage" class="mb-5 pb-4">
                    <SuccessMessage :notification="verifiedMsg" />
                </div>
                <div v-if="showPasswordResetMessage" class="mb-5 pb-4">
                    <SuccessMessage :notification="passwordResetMsg" />
                </div>
            </div>

            <div v-if="LoginMsg" class="mb-5 pb-4 d-none d-sm-none d-md-none d-lg-block d-xl-block">
                <Message :notification="notification" />
            </div>
            <div v-if="LoginErrorMsg" class="mb-5 pb-4 d-none d-sm-none d-md-none d-lg-block d-xl-block">
                <ErrorMessage :errorNotification="errorNotification" />
            </div>

            <div class="row">
                <div class="col-12 col-sm-12 col-md-12 col-lg-6 col-xl-6 col-right-padding">
                    <h1 class="title pb-3 mb-3">{{ registerMsg }}</h1>
                    <p v-if="!isMyBeauty" class="text pb-3 pb-lg-0 mb-3 mb-lg-5">
                        <a href="https://www.harrods.com/en-gb/harrods-rewards" target="_blank">
                            <span class="hyperlink">Find out more</span>
                        </a>
                        about the Harrods Rewards programme.
                    </p>
                    <p v-else class="text pb-3 pb-lg-0 mb-3 mb-lg-5">
                        Want to become a MyBeauty member? Create a Harrods Rewards
                        account to join MyBeauty and access MyBeauty perks, including first access to new launches, exclusive offers
                        and promotions, and exciting event invitations.<br />
                        <a href="https://www.harrods.com/en-gb/harrods-rewards/mybeauty" target="_blank">
                            <span class="hyperlink signup-links">Find out more about the MyBeauty programme.</span>
                        </a>
                        <a href="https://www.harrods.com/en-gb/legal/my-beauty" target="_blank">
                            <span class="hyperlink signup-links">Ts&Cs apply</span>
                        </a>.
                    </p>
                    <button type="submit" class="btn btn-primary green-btn mb-5" :class="!isMyBeauty ? 'register-btn' : ''"
                            @click="goToJoinRegister">
                        <span v-if="!isMyBeauty">Join Harrods Rewards</span>
                        <span v-if="isMyBeauty">Join Harrods Rewards & MyBeauty</span>
                    </button>
                    <div class="d-none d-sm-none d-md-none d-lg-block d-xl-block">
                        <p class="text mt-3">
                            Have a Rewards card but don't have an online account? Join and Add your Existing Number
                            during Registration.
                        </p>
                    </div>
                </div>
                <div class="col-12 col-sm-12 col-md-12 col-lg-6 col-xl-6 col-right-padding">
                    <h2 v-if="!isMyBeauty" class="title pb-3 mb-3 mt-3 mt-lg-0 mt-xl-0">Sign in</h2>
                    <h2 v-else class="title pb-3 mb-3 mt-3 mt-lg-0 mt-xl-0">Already have a harrods.com account?</h2>
                    <p class="text pb-3 pb-lg-0 mb-3 mb-lg-5">
                        Sign in to your Existing Account to Download your card. Have an Account without Rewards? Sign in and Upgrade today.
                    </p>
                    <SignIn @passLoginErrorMsg="displayLoginErrorMsg" :source="source" :prefillEmail="email" />
                    <div class="d-block d-sm-block d-md-block d-lg-none d-xl-none">
                        <p class="text mt-3">Have a Rewards card but don't have an online account? Join and Add your Existing Number during Registration.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, inject, provide } from 'vue'
    import { useRoute, useRouter } from 'vue-router'
    import Banner from '@/components/Banner.vue'
    import Message from '@/components/Message.vue'
    import SuccessMessage from '@/components/SuccessMessage.vue'
    import ErrorMessage from '@/components/ErrorMessage.vue'
    import SignIn from '@/components/SignIn.vue'
    import integrationApi from '@/services/IntegrationApiClient.js'

    // Props
    const props = defineProps(['showVerifiedMessage', 'showVerifiedErrorMessage', 'showPasswordResetMessage'])

    // Router
    const route = useRoute()
    const router = useRouter()

    // State
    const bannerMessage = ref('Your Rewards card is waiting')
    const registerMsg = ref('')
    const notification = ref("To download your digital Rewards card, it's best to sign in on a mobile device.")
    const errorVerifiedMsg = ref("Sorry, we haven't been able confirm whether your account is verified. <br />If you believe you have verified, please continue to sign-in")
    const verifiedMsg = ref("Your account has been verified successfully")
    const errorNotification = ref('')
    const passwordResetMsg = ref("Your password has been successfully reset. Please login with your new password.")
    const LoginMsg = ref(true)
    const LoginErrorMsg = ref(false)
    const source = computed(() => route.query.l)
    const email = ref('')
    const marketingOptIn = ref(false)

    // isMyBeauty logic (use inject if provided globally, else fallback to route/query)
    const isMyBeauty = inject('isMyBeauty', computed(() =>
        route.params.isMyBeauty === 'mybeauty' ||
        route.query.isMyBeauty === 'true' ||
        route.query.isMyBeauty === true 
    ))
    provide('isMyBeauty', isMyBeauty.value)

    function displayLoginErrorMsg(errorMsg) {
        LoginMsg.value = false
        LoginErrorMsg.value = true
        errorNotification.value = errorMsg
    }

    function goToJoinRegister() {
        router.push({
            name: 'JoinRewardsRegister',
            params: {
                source: source.value,
                email: email.value,
                marketingOptIn: marketingOptIn.value,
                isMyBeauty: isMyBeauty.value ? 'mybeauty' : undefined
            }
        })
    }

    function decryptPurplePayload() {
        if (route.query.u !== undefined) {
            integrationApi.decryptVal(route.query.u).then(result => {
                email.value = result.email
                marketingOptIn.value = result.optedIn === true
            })
        }
    }

    onMounted(() => {
        registerMsg.value = isMyBeauty.value
            ? 'Join Harrods Rewards & MyBeauty'
            : 'Join Harrods Rewards'
        decryptPurplePayload()
    })
</script>

<style lang="scss" scoped>
.title {
  @media screen and (max-width: 767px) and (min-width: 0px) {
    font-size: 24px;
  }

  @media screen and (max-width: 991px) and (min-width: 768px) {
    font-size: 26px;
  }

  @media screen and (min-width: 992px) {
    font-size: 30px;
  }
}

.container {
  background-color: $harrods-white;

  @media screen and (max-width: 1199px) and (min-width: 992px) {
    padding-right: 50px;
    padding-left: 50px;
  }

  @media screen and (min-width: 1200px) {
    padding-right: 75px;
    padding-left: 75px;
  }
}

    .col-right-padding {
        @media screen and (max-width: 1199px) and (min-width: 992px) {
            padding-right: 70px;
        }

        @media screen and (min-width: 1200px) {
            padding-left: 15px;
            padding-right: 15px;
        }
    }

.signup-links {
  font-weight: 300;
  line-height: 24px;
}

.register-btn {
  @media screen and (max-width: 1199px) and (min-width: 992px) {
    margin-top: 45px;
  }

  @media screen and (min-width: 1200px) {
    margin-top: 22.5px;
  }
}
</style>
